import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { CheckImageData } from '../check-deposit.model';
import { CheckIntakeProvider, IntakeStatusDetail } from './check-intake.provider';
import { SecureLink2, SL2Connection, SL2GetDocumentResponse, isSL2FeederEmpty } from './securelink-sdk';

/**
 * TellerScanProvider — implements CheckIntakeProvider for Digital Check
 * TellerScan TS250 via the SecureLink 2.0 SDK (securelink-sdk.ts).
 *
 * Workflow per SecureLink 2.0 Getting Started Guide v1.1:
 *   connect() → SetParameters → GetDocument (×N) → StopFlow → disconnect()
 *
 * MICR field mapping:
 *   Fld5 = routing/transit number
 *   Fld3 = account number
 *   Fld2 = check number
 *   RawMicr = full MICR line
 */
@Injectable()
export class TellerScanProvider implements CheckIntakeProvider {
  readonly providerType = 'scanner' as const;
  readonly displayName = 'TellerScan TS250';

  private _status: IntakeStatusDetail = { status: 'disconnected' };
  private _scannerBaseUrl = 'https://192.168.1.100';
  private _connection: SL2Connection | null = null;
  private _scannerModel = '';
  private _scannerSerial = '';
  private _keepAliveInterval: ReturnType<typeof setInterval> | null = null;

  /** When true, all hardware calls are skipped and fake check data is returned. */
  private readonly _mockMode: boolean = (environment as Record<string, unknown>)['mockScannerHardware'] === true;

  // ── Mock helpers ───────────────────────────────────────────────────────────

  private _mockBatchIndex = 0;

  private readonly _mockChecks = [
    { checkNumber: '1001', routing: '021000021', account: '1234567890', writer: 'Acme Corp', amount: 125000 },
    { checkNumber: '1042', routing: '021000021', account: '9876543210', writer: 'Beta Industries', amount: 85050 },
    { checkNumber: '2200', routing: '322271627', account: '5555123400', writer: 'Gamma LLC', amount: 310000 },
    { checkNumber: '0077', routing: '111000025', account: '2468013579', writer: 'Delta Suppliers', amount: 42500 },
    { checkNumber: '0500', routing: '021000021', account: '1111222233', writer: 'Epsilon Co', amount: 200000 },
  ];

  /**
   * Draws a simple check placeholder on a canvas and returns its base64 JPEG.
   * Produces a visually distinct image per check index so the queue isn't
   * all identical thumbnails during testing.
   */
  private _buildMockImage(label: string, index: number): string {
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 300;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    const bg = ['#f5f5f0', '#f0f5ff', '#fff5f0', '#f5fff0', '#fff0f5'][index % 5];
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, 640, 300);

    ctx.strokeStyle = '#aaa';
    ctx.lineWidth = 2;
    ctx.strokeRect(4, 4, 632, 292);

    ctx.fillStyle = '#e5e7eb';
    ctx.fillRect(4, 4, 200, 60);

    ctx.fillStyle = '#374151';
    ctx.font = 'bold 13px Arial';
    ctx.fillText('MOCK SCANNER — DEV ONLY', 12, 22);
    ctx.font = 'bold 18px Arial';
    ctx.fillText(label, 12, 50);

    ctx.fillStyle = '#6b7280';
    ctx.font = '14px Arial';
    ctx.fillText('Pay to the order of ___________________________', 20, 110);
    ctx.fillText('Amount: ______________', 20, 150);
    ctx.fillText('Memo: ________________', 20, 190);

    ctx.font = '16px Courier New';
    ctx.fillStyle = '#1a1a1a';
    const check = this._mockChecks[index % this._mockChecks.length];
    ctx.fillText(`⑆${check.routing}⑆  ⑈${check.account}⑈  ${check.checkNumber}⑇`, 20, 270);

    return canvas.toDataURL('image/jpeg', 0.85).split(',')[1];
  }

  private _buildMockCheckImageData(index: number): CheckImageData {
    const check = this._mockChecks[index % this._mockChecks.length];
    const frontImage = this._buildMockImage(`Check #${check.checkNumber} — ${check.writer}`, index);
    const backImage = this._buildMockImage(`BACK — Endorse Here`, index);
    const raw = `⑆${check.routing}⑆⑈${check.account}⑈${check.checkNumber}⑇`;

    return {
      frontImage,
      backImage,
      imageType: 'jpg',
      frontImageSize: Math.round(frontImage.length * 0.75),
      backImageSize: Math.round(backImage.length * 0.75),
      captureSource: 'scanner_tellerscan',
      controlChar: 'S',
      micrData: raw,
      micrRouting: check.routing,
      micrAccount: check.account,
      micrCheckNumber: check.checkNumber,
      imageResolutionDpi: 200,
      scannerDeviceId: 'MOCK-TS250-SN00001',
    };
  }

  private _mockDelay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private _parseDpi(resolution: string): number {
    const match = resolution?.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 200;
  }

  private _startKeepAlive(): void {
    this._stopKeepAlive();
    // Poll GetParameters every 2 minutes to keep the idle session alive.
    this._keepAliveInterval = setInterval(async () => {
      if (!this._connection) return;
      try {
        await this._connection.getParams('ScannerModel');
      } catch {
        // If the scanner is unreachable the next real command will surface it.
      }
    }, 120_000);
  }

  private _stopKeepAlive(): void {
    if (this._keepAliveInterval) {
      clearInterval(this._keepAliveInterval);
      this._keepAliveInterval = null;
    }
  }

  private _mapImageType(format: string): 'tiff' | 'png' | 'jpg' {
    if (format === 'g4tiff' || format === 'jpegtiff') return 'tiff';
    if (format === 'jpeg') return 'jpg';
    return 'jpg';
  }

  private _buildCheckImageData(doc: SL2GetDocumentResponse): CheckImageData {
    const images = doc.Images ?? [];

    // Prefer greyscale for browser display; fall back to B&W TIFF.
    const frontImg = images.find((i) => i.ImageType === 'fgs') ?? images.find((i) => i.ImageType === 'fbw');
    const rearImg = images.find((i) => i.ImageType === 'rgs') ?? images.find((i) => i.ImageType === 'rbw');

    if (!frontImg || !rearImg) {
      throw new Error('Scanner did not return both front and rear images.');
    }

    const micr = doc.MicrFields ?? {};
    const scannerDeviceId = this._scannerSerial ? `${this._scannerModel}-SN${this._scannerSerial}` : this._scannerModel;

    return {
      frontImage: frontImg.data,
      backImage: rearImg.data,
      imageType: this._mapImageType(frontImg.ImageFormat),
      frontImageSize: Math.round(frontImg.data.length * 0.75),
      backImageSize: Math.round(rearImg.data.length * 0.75),
      captureSource: 'scanner_tellerscan',
      controlChar: 'S',
      micrData: (micr.RawMicr ?? '').trim() || undefined,
      micrRouting: (micr.Fld5 ?? '').trim() || undefined,
      micrAccount: (micr.Fld3 ?? '').trim() || undefined,
      micrCheckNumber: (micr.Fld2 ?? '').trim() || undefined,
      imageResolutionDpi: this._parseDpi(frontImg.ImageResolution),
      scannerDeviceId,
    };
  }

  // ── CheckIntakeProvider: Lifecycle ─────────────────────────────────────────

  async connect(): Promise<IntakeStatusDetail> {
    if (this._mockMode) {
      await this._mockDelay(600);
      this._mockBatchIndex = 0;
      this._status = {
        status: 'ready',
        deviceInfo: {
          model: 'TS250 (Mock)',
          serialNumber: 'MOCK-00001',
          firmwareVersion: '2.0.0-mock',
          ipAddress: '127.0.0.1',
        },
      };
      return this._status;
    }

    try {
      const scanner = SecureLink2(this._scannerBaseUrl);

      // Step 1: Open an exclusive session with a 5-minute idle timeout.
      const { connection, connectResponse } = await scanner.connect({
        Exclusive: 'true',
        IdleTimeout: '300',
      });

      this._connection = connection;
      this._scannerModel = connectResponse.ScannerModel ?? 'Unknown';

      // Step 2: Reset parameters to defaults (recommended by docs).
      await connection.setParams({});

      // Step 3: Configure for check scanning — greyscale + B&W + MICR.
      await connection.setParams({
        fgsEnabled: 'true',
        rgsEnabled: 'true',
        fbwEnabled: 'true',
        rbwEnabled: 'true',
        FeedType: 'single',
        MicrEnabled: 'true',
      });

      // Step 4: Retrieve device details for the status readout.
      const paramsRes = await connection.getParams('ScannerModel');
      const paramsObj = paramsRes.Params as Record<string, string> | undefined;
      this._scannerSerial = paramsObj?.['Serial'] ?? '';

      this._status = {
        status: 'ready',
        deviceInfo: {
          model: this._scannerModel,
          serialNumber: this._scannerSerial,
          firmwareVersion: connectResponse.VersionObject?.Version ?? '',
          ipAddress: this._scannerBaseUrl.replace(/^https?:\/\//, ''),
        },
      };

      this._startKeepAlive();
      return this._status;
    } catch (err: unknown) {
      this._connection = null;
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'ErrorInformation' in err
          ? String((err as Record<string, unknown>)['ErrorInformation'])
          : 'Failed to connect to scanner';

      this._status = { status: 'error', errorMessage: message };
      return this._status;
    }
  }

  disconnect(): void {
    if (this._mockMode) {
      this._status = { status: 'disconnected' };
      return;
    }

    this._stopKeepAlive();

    if (this._connection) {
      // Fire-and-forget: best-effort StopFlow then Disconnect.
      this._connection.stopFlow().catch(() => {});
      this._connection.disconnect().catch(() => {});
      this._connection = null;
    }

    this._status = { status: 'disconnected' };
  }

  getStatus(): IntakeStatusDetail {
    return this._status;
  }

  // ── CheckIntakeProvider: Single check ─────────────────────────────────────

  async capture(): Promise<CheckImageData> {
    if (this._mockMode) {
      this._status = { ...this._status, status: 'scanning' };
      await this._mockDelay(1500); // Simulate scanner processing time
      const data = this._buildMockCheckImageData(this._mockBatchIndex++);
      this._status = { ...this._status, status: 'ready' };
      return data;
    }

    if (!this._connection) {
      throw new Error('Scanner not connected. Call connect() first.');
    }

    this._status = { ...this._status, status: 'scanning' };

    try {
      const result = await this._connection.getDocument({
        IncludeImages: 'true',
        Timeout: '10000',
      });

      if (isSL2FeederEmpty(result)) {
        throw new Error('Feeder empty — place a check in the scanner and try again.');
      }

      await this._connection.stopFlow();

      const data = this._buildCheckImageData(result);
      this._status = { ...this._status, status: 'ready' };
      return data;
    } catch (err) {
      this._status = {
        ...this._status,
        status: 'error',
        errorMessage: (err as Error).message,
      };
      throw err;
    }
  }

  // ── CheckIntakeProvider: Batch capture ────────────────────────────────────

  async *captureBatch(): AsyncIterable<CheckImageData> {
    if (this._mockMode) {
      this._status = { ...this._status, status: 'scanning' };
      const count = this._mockChecks.length;
      try {
        for (let i = 0; i < count; i++) {
          await this._mockDelay(1200); // ~1.2s per check, like a real feeder
          yield this._buildMockCheckImageData(this._mockBatchIndex++);
        }
      } finally {
        this._status = { ...this._status, status: 'ready' };
      }
      return;
    }

    if (!this._connection) {
      throw new Error('Scanner not connected. Call connect() first.');
    }

    // Switch to batch feed mode for the duration of this session.
    await this._connection.setParams({ FeedType: 'batch' });
    this._status = { ...this._status, status: 'scanning' };

    try {
      while (true) {
        const result = await this._connection.getDocument({
          IncludeImages: 'true',
          Timeout: '3000',
        });

        if (isSL2FeederEmpty(result)) break;

        try {
          yield this._buildCheckImageData(result as SL2GetDocumentResponse);
        } catch {
          // Skip malformed document (missing images) and continue.
        }
      }
    } finally {
      await this._connection.stopFlow().catch(() => {});
      // Restore single-feed mode so single capture works after a batch run.
      await this._connection.setParams({ FeedType: 'single' }).catch(() => {});
      this._status = { ...this._status, status: 'ready' };
    }
  }

  /**
   * Request the active batch scan to stop after the current document.
   * Calls StopFlow on the connection; the captureBatch() generator exits on
   * the next loop iteration when isSL2FeederEmpty detects the closed session.
   */
  stopCapture(): void {
    if (this._mockMode) {
      this._status = { ...this._status, status: 'ready' };
      return;
    }
    if (this._connection) {
      this._connection.stopFlow().catch(() => {});
    }
  }

  supportsAutoMicr(): boolean {
    return true;
  }

  supportsBatchFeed(): boolean {
    return true;
  }

  // ── Configuration ─────────────────────────────────────────────────────────

  setScannerBaseUrl(url: string): void {
    this._scannerBaseUrl = url.replace(/\/+$/, '');
  }

  getScannerBaseUrl(): string {
    return this._scannerBaseUrl;
  }
}
